import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { User } from "../graphql/graphql";
import type { OktaAuth } from '@okta/okta-auth-js';
import { GET_CURRENT_USER, UPDATE_USER_TERRITORIES } from "../graphql/users.graphql";
import { GqlErrorModal } from "./GqlErrorModal";

const E2E_AUTH_BYPASS = process.env.REACT_APP_E2E_AUTH_BYPASS === '1';

interface UserSettingsContextType {
  user: User | undefined;
  availableTerritories: string[];
  currentTerritories: string[];
  setCurrentTerritories: (territories: string[]) => void;
  loading: boolean;
  error: any;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

export function UserSettingsProvider({ children, oktaAuth }: { children: React.ReactNode, oktaAuth: OktaAuth }) {
  // Immediately mark authenticated in bypass mode to avoid initial query skip
  const [isAuthenticated, setIsAuthenticated] = useState(E2E_AUTH_BYPASS);
  const [authState, setAuthState] = useState<any>(E2E_AUTH_BYPASS ? { isAuthenticated: true, accessToken: 'dummy-access', isPending: false } : null);
  const [modalError, setModalError] = useState<any>(null);

  useEffect(() => {
    if (E2E_AUTH_BYPASS) {
      // Short-circuit auth: mark authenticated immediately
      const mockState = { isAuthenticated: true, accessToken: 'dummy-access', isPending: false };
      setAuthState(mockState);
      setIsAuthenticated(true);
      return; // Skip real Okta listeners
    }
    const getInitialAuthState = async () => {
      try {
        const authenticated = await oktaAuth.isAuthenticated();
        const accessToken = oktaAuth.getAccessToken();
        const currentAuthState = {
          isAuthenticated: authenticated,
          accessToken: accessToken,
          isPending: false
        };
        setAuthState(currentAuthState);
        setIsAuthenticated(authenticated && !!accessToken);
      } catch (err) {
        console.error("Error checking authentication status:", err);
        setIsAuthenticated(false);
        setAuthState({ isAuthenticated: false, accessToken: null, isPending: false });
      }
    };
    const handleAuthStateChange = (newAuthState: any) => {
      setAuthState(newAuthState);
      const fullyAuthenticated = newAuthState.isAuthenticated && !!newAuthState.accessToken;
      setIsAuthenticated(fullyAuthenticated);
    };
    oktaAuth.authStateManager.subscribe(handleAuthStateChange);
    getInitialAuthState();
    return () => {
      oktaAuth.authStateManager.unsubscribe(handleAuthStateChange);
    };
  }, [oktaAuth]);

  const { data, loading, error, refetch } = useQuery(GET_CURRENT_USER, {
    skip: !isAuthenticated,
    fetchPolicy: E2E_AUTH_BYPASS ? 'cache-first' : 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // Refetch when authentication becomes available
  useEffect(() => {
    if (E2E_AUTH_BYPASS) return; // no refetch loops in bypass
    if (isAuthenticated && !authState?.isPending && refetch) {
      refetch().catch(err => {
        setModalError(err);
      });
    }
  }, [isAuthenticated, authState?.isPending, refetch]);

  const user: User | undefined = data?.user?.me;
  const availableTerritories = user?.settings?.availableTerritories || (E2E_AUTH_BYPASS ? ['US'] : []);
  const [currentTerritories, setCurrentTerritoriesState] = useState<string[]>(
    user?.settings?.selectedTerritories || (E2E_AUTH_BYPASS ? ['US'] : [])
  );

  const [updateUserSettings] = useMutation(UPDATE_USER_TERRITORIES);

  useEffect(() => {
    if (user?.settings?.selectedTerritories) {
      setCurrentTerritoriesState(user.settings.selectedTerritories);
    }
  }, [user?.settings?.selectedTerritories]);

  const setCurrentTerritories = (territories: string[]) => {
    setCurrentTerritoriesState(territories);
    if (!E2E_AUTH_BYPASS) {
      updateUserSettings({ variables: { selectedTerritories: territories } });
    }
  };

  // Memoize user and currentTerritories
  const memoizedUser = useMemo(() => user, [user]);
  const memoizedCurrentTerritories = useMemo(() => currentTerritories, [currentTerritories]);

  return (
    <UserSettingsContext.Provider
      value={{
        user: memoizedUser,
        availableTerritories,
        currentTerritories: memoizedCurrentTerritories,
        setCurrentTerritories,
        loading: loading || authState?.isPending,
        error
      }}
    >
      {children}
      {modalError && (
        <GqlErrorModal
          error={modalError}
          onClose={() => setModalError(null)}
        />
      )}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettings() {
  const ctx = useContext(UserSettingsContext);
  if (!ctx) throw new Error("useUserSettings must be used within a UserSettingsProvider");
  return ctx;
}